import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Trash, CircleNotch, ArrowSquareOut, TrendDown, Scales, Check, BellRinging } from '@phosphor-icons/react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const flagEmoji = { AU: '\uD83C\uDDE6\uD83C\uDDFA', UK: '\uD83C\uDDEC\uD83C\uDDE7', US: '\uD83C\uDDFA\uD83C\uDDF8' };

export default function WishlistPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [compareIds, setCompareIds] = useState([]);
  const [digestOn, setDigestOn] = useState(null);  // null = unknown, true/false once loaded
  const [digestSaving, setDigestSaving] = useState(false);
  const { user } = useAuth();
  const { format, region } = useCurrency();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/wishlist`, { params: { region }, withCredentials: true }),
      axios.get(`${API}/alerts/price-drops`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/email/preferences`, { withCredentials: true }).catch(() => ({ data: null })),
    ]).then(([itemsRes, alertsRes, prefsRes]) => {
      setItems(itemsRes.data);
      setAlerts(alertsRes.data);
      if (prefsRes.data) setDigestOn(!!prefsRes.data.weekly_digest);
    }).catch(console.error).finally(() => setLoading(false));
  }, [region]);

  const toggleDigest = async () => {
    if (digestSaving) return;
    const next = !digestOn;
    setDigestSaving(true);
    setDigestOn(next);  // optimistic
    try {
      await axios.put(`${API}/email/preferences`, { weekly_digest: next }, { withCredentials: true });
    } catch (e) {
      setDigestOn(!next);  // rollback
      console.error(e);
    } finally {
      setDigestSaving(false);
    }
  };

  const removeItem = async (shoeId) => {
    try {
      await axios.delete(`${API}/wishlist/${shoeId}`, { withCredentials: true });
      setItems(items.filter(s => s.shoe_id !== shoeId));
      setCompareIds(compareIds.filter(id => id !== shoeId));
      window.dispatchEvent(new Event('wishlist-updated'));
    } catch (e) { console.error(e); }
  };

  const handleBuyClick = async (shoeId, storeId, productUrl) => {
    try {
      const { data } = await axios.post(`${API}/affiliate/click`, { shoe_id: shoeId, store_id: storeId }, { withCredentials: true });
      window.open(data.affiliate_url || productUrl, '_blank');
    } catch {
      window.open(productUrl, '_blank');
    }
  };

  const toggleCompare = (shoeId) => {
    if (compareIds.includes(shoeId)) {
      setCompareIds(compareIds.filter(id => id !== shoeId));
    } else if (compareIds.length < 4) {
      setCompareIds([...compareIds, shoeId]);
    }
  };

  const goCompare = () => {
    // Navigate to compare page with pre-selected shoes
    navigate('/compare', { state: { preSelected: compareIds } });
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><CircleNotch size={48} className="animate-spin text-[#FF3B30]" /></div>;

  return (
    <div className="min-h-screen bg-white" data-testid="wishlist-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Heart size={32} weight="duotone" className="text-[#FF3B30]" />
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">Your Collection</p>
              <h1 className="font-heading font-black text-3xl tracking-tighter uppercase" data-testid="wishlist-title">Wishlist</h1>
            </div>
          </div>

          {/* Weekly digest opt-in toggle */}
          {user && digestOn !== null && items.length > 0 && (
            <button
              onClick={toggleDigest}
              disabled={digestSaving}
              className={`group inline-flex items-center gap-2 pl-3 pr-4 py-2 border-2 text-xs font-bold uppercase tracking-wide transition-colors ${digestOn ? 'border-[#10B981] bg-[#10B981] text-white hover:bg-[#0d9568]' : 'border-[#E4E4E7] bg-white text-[#0A0A0A] hover:border-[#FF3B30]'}`}
              data-testid="wishlist-digest-toggle"
              title={digestOn ? 'Weekly drop alerts are ON — tap to turn off' : 'Get a Monday email when shoes on your wishlist drop in price'}
            >
              <BellRinging size={16} weight={digestOn ? 'fill' : 'regular'} />
              <span>Weekly drop alerts:</span>
              <span className={digestOn ? '' : 'text-[#FF3B30]'} data-testid="wishlist-digest-state">
                {digestOn ? 'ON' : 'OFF'}
              </span>
            </button>
          )}

          {/* Compare bar */}
          {items.length >= 2 && (
            <div className="flex items-center gap-3">
              {compareIds.length > 0 && (
                <span className="text-xs font-bold uppercase tracking-wide text-[#52525B]">{compareIds.length} selected</span>
              )}
              <button
                onClick={goCompare}
                disabled={compareIds.length < 2}
                className={`inline-flex items-center gap-2 font-bold uppercase tracking-wide text-xs px-6 py-3 transition-colors ${compareIds.length >= 2 ? 'bg-[#FF3B30] text-white hover:bg-[#D62828]' : 'bg-[#E4E4E7] text-[#A1A1AA] cursor-not-allowed'}`}
                data-testid="wishlist-compare-btn">
                <Scales size={16} /> Compare Selected ({compareIds.length}/4)
              </button>
              {compareIds.length > 0 && (
                <button onClick={() => setCompareIds([])}
                  className="text-xs font-bold uppercase tracking-wide text-[#52525B] hover:text-[#FF3B30] transition-colors"
                  data-testid="wishlist-clear-compare">
                  Clear
                </button>
              )}
            </div>
          )}
        </div>

        {/* Price Drop Alerts */}
        {alerts.filter(a => !a.read).length > 0 && (
          <div className="mb-8 border-2 border-[#10B981] bg-green-50 p-4" data-testid="price-drop-alerts">
            <div className="flex items-center gap-2 mb-3">
              <TrendDown size={24} weight="duotone" className="text-[#10B981]" />
              <h3 className="font-heading font-bold text-lg">Price Drop Alerts</h3>
              <span className="bg-[#10B981] text-white text-xs font-bold px-2 py-0.5">{alerts.filter(a => !a.read).length} NEW</span>
            </div>
            <div className="space-y-2">
              {alerts.filter(a => !a.read).slice(0, 5).map(alert => (
                <div key={alert.alert_id} className="flex items-center justify-between bg-white p-3 border border-[#E4E4E7]">
                  <div>
                    <p className="font-bold text-sm">{alert.shoe_name}</p>
                    <p className="text-xs text-[#52525B]">{alert.store_name} &middot; <span className="line-through">{format(alert.old_price, alert.currency)}</span> <span className="text-[#FF3B30] font-bold">{format(alert.new_price, alert.currency)}</span> <span className="text-[#10B981] font-bold">(-{alert.drop_pct}%)</span></p>
                  </div>
                  <a href={alert.product_url} target="_blank" rel="noopener noreferrer" className="bg-[#FF3B30] text-white text-xs font-bold uppercase px-3 py-1.5 hover:bg-[#D62828] transition-colors flex items-center gap-1">
                    Shop <ArrowSquareOut size={12} />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {items.length === 0 ? (
          <div className="text-center py-20 border border-[#E4E4E7]" data-testid="wishlist-empty">
            <Heart size={48} className="text-[#E4E4E7] mx-auto mb-4" />
            <p className="text-lg text-[#52525B]">Your wishlist is empty</p>
            <Link to="/quiz" className="inline-flex items-center gap-2 mt-4 bg-[#FF3B30] text-white font-bold uppercase px-8 py-4 hover:bg-[#D62828] transition-colors">
              Find Your Fit
            </Link>
          </div>
        ) : (
          <>
            {items.length >= 2 && (
              <p className="text-xs text-[#52525B] mb-4">Select 2-4 shoes to compare them side by side</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {items.map((shoe, i) => {
                const lowestPrice = shoe.store_prices?.length > 0 ? shoe.store_prices.reduce((a, b) => a.price < b.price ? a : b) : null;
                const isSelected = compareIds.includes(shoe.shoe_id);
                return (
                  <motion.div key={shoe.shoe_id}
                    className={`border-2 bg-white transition-all group relative ${isSelected ? 'border-[#FF3B30] ring-2 ring-[#FF3B30]/20' : 'border-[#E4E4E7] hover:border-[#0A0A0A]'}`}
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    data-testid={`wishlist-item-${shoe.shoe_id}`}>

                    {/* Compare checkbox */}
                    <button
                      onClick={() => toggleCompare(shoe.shoe_id)}
                      className={`absolute top-3 left-3 z-10 w-7 h-7 flex items-center justify-center border-2 transition-all ${isSelected ? 'bg-[#FF3B30] border-[#FF3B30] text-white' : 'bg-white/80 border-[#D4D4D8] hover:border-[#FF3B30]'} ${!isSelected && compareIds.length >= 4 ? 'opacity-40 cursor-not-allowed' : ''}`}
                      disabled={!isSelected && compareIds.length >= 4}
                      data-testid={`wishlist-compare-check-${shoe.shoe_id}`}>
                      {isSelected && <Check size={16} weight="bold" />}
                    </button>

                    <button onClick={() => removeItem(shoe.shoe_id)} className="absolute top-3 right-3 z-10 p-2 bg-white/80 hover:bg-[#FF3B30] hover:text-white transition-colors" data-testid={`wishlist-remove-${shoe.shoe_id}`}>
                      <Trash size={18} />
                    </button>
                    <Link to={`/shoe/${shoe.shoe_id}`}>
                      <img src={shoe.image_url} alt={shoe.model} className="w-full aspect-[4/3] object-cover" />
                    </Link>
                    <div className="p-4">
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">{shoe.brand}</p>
                      <h3 className="font-heading font-bold text-lg mt-1">{shoe.model}</h3>
                      {lowestPrice && (
                        <p className="font-bold text-lg mt-2">From {format(lowestPrice.price, lowestPrice.currency)}</p>
                      )}
                      {/* Quick store links */}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(shoe.store_prices || []).slice(0, 3).map((p, j) => (
                          <button key={j} onClick={() => handleBuyClick(shoe.shoe_id, p.store_id, p.product_url)}
                            className="text-xs bg-[#F4F4F5] border border-[#E4E4E7] px-3 py-1.5 hover:bg-[#FF3B30] hover:text-white hover:border-[#FF3B30] transition-colors flex items-center gap-1"
                            data-testid={`wishlist-buy-${shoe.shoe_id}-${p.store_id}`}>
                            {flagEmoji[p.store_region]} {p.store_name} <ArrowSquareOut size={12} />
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Sticky compare bar at bottom */}
      {compareIds.length >= 2 && (
        <motion.div
          initial={{ y: 100 }} animate={{ y: 0 }}
          className="fixed bottom-0 left-0 right-0 bg-[#0A0A0A] text-white py-4 px-6 z-50 flex items-center justify-between"
          data-testid="wishlist-compare-sticky">
          <div className="flex items-center gap-4">
            <Scales size={20} />
            <span className="text-sm font-bold">{compareIds.length} shoes selected for comparison</span>
            <div className="flex gap-2">
              {compareIds.map(id => {
                const shoe = items.find(s => s.shoe_id === id);
                return shoe ? (
                  <div key={id} className="flex items-center gap-1 bg-white/10 px-2 py-1 text-xs">
                    <img src={shoe.image_url} alt="" className="w-5 h-5 object-cover" />
                    <span>{shoe.brand}</span>
                    <button onClick={() => toggleCompare(id)} className="hover:text-[#FF3B30] ml-1">&times;</button>
                  </div>
                ) : null;
              })}
            </div>
          </div>
          <button onClick={goCompare}
            className="bg-[#FF3B30] text-white font-bold uppercase tracking-wide text-sm px-8 py-3 hover:bg-[#D62828] transition-colors flex items-center gap-2"
            data-testid="wishlist-compare-go">
            <Scales size={16} /> Compare Now
          </button>
        </motion.div>
      )}
    </div>
  );
}
